import { type FC } from "react";

import { Button } from "@snapchat/snap-design-system";
import { UploadIcon } from "@snapchat/snap-design-system-icons";

import { type LensPreviewStateHook } from "../../../../lens-preview/hooks";
import { Flexbox } from "../../../../shared/components";
import { defaultPreviewMediaItem, isDefaultItem } from "../../../../shared/state";

import styles from "./GameLensInformationPanel.module.css";
import {
    ChangeGameplayVideoButton,
    GameLensInformationDescription,
    GameplayPreviewLabel,
    UploadGameplayVideoButton,
} from "./messages.generated";

interface GameLensInformationPanelProps {
    lensPreviewProps: Pick<LensPreviewStateHook, "previewMediaItems" | "showModal" | "validationMessage">;
}

export const GameLensInformationPanel: FC<GameLensInformationPanelProps> = ({ lensPreviewProps }) => {
    const previewMediaItem = lensPreviewProps.previewMediaItems[0] ?? defaultPreviewMediaItem;
    const hasPreview = Boolean(!isDefaultItem(previewMediaItem) && previewMediaItem.url);
    const previewName = previewMediaItem.name;

    return (
        <Flexbox className={styles.panel} data-testid="game-lens-information-panel" flexDirection="column" gap="8px">
            <p className={styles.sectionLabel} data-testid="gameplay-preview-label">
                <GameplayPreviewLabel />
            </p>
            <Flexbox className={styles.mediaRow} alignItems="center" gap="16px">
                <div className={styles.previewFrame} data-testid="gameplay-preview-thumbnail">
                    {hasPreview &&
                        (previewMediaItem.isVideo ? (
                            <video
                                className={styles.previewMedia}
                                src={previewMediaItem.url}
                                muted={true}
                                playsInline={true}
                                preload="metadata"
                            />
                        ) : (
                            <img className={styles.previewMedia} src={previewMediaItem.url} alt="" />
                        ))}
                </div>
                <Flexbox className={styles.mediaContent} flex="1" flexDirection="column" gap="8px">
                    {previewName && <p className={styles.previewName}>{previewName}</p>}
                    <Button
                        type="primary"
                        className={styles.previewButton}
                        data-testid="gameplay-preview-button"
                        onClick={lensPreviewProps.showModal}
                    >
                        <UploadIcon />
                        {hasPreview ? <ChangeGameplayVideoButton /> : <UploadGameplayVideoButton />}
                    </Button>
                </Flexbox>
            </Flexbox>
            {lensPreviewProps.validationMessage && (
                <p className={styles.validationMessage}>{lensPreviewProps.validationMessage}</p>
            )}
            <p className={styles.description}>
                <GameLensInformationDescription />
            </p>
        </Flexbox>
    );
};
